// MasterPasswordTab — Settings surface for changing the master password.
//
// Three fields (current, new, confirm), zxcvbn ≥ 3 gate on `new`, and
// a "Generate a passphrase" link that pops the passphrase generator
// directly into the `new master` field. Submission delegates to a
// parent-provided `onRotationStart(currentPass, newPass)` — the
// parent (`Settings`) owns the rotation modal mount + progress state.
//
// Layout follows the tab-file shape established by `CacheTab.tsx` and
// `SecurityTab.tsx`. Like `MasterSetupWizard.tsx`, the zxcvbn memo is
// declared above any conditional return path to satisfy React's
// Rules of Hooks.

import { useMemo, useState } from "react";
import { zxcvbn } from "@zxcvbn-ts/core";
import { PassphraseGenerator } from "../primitives/PassphraseGenerator";
import { PassphraseInput } from "../primitives/PassphraseInput";
import { PassphraseStrength } from "../primitives/PassphraseStrength";

export interface MasterPasswordTabProps {
  /**
   * Fired when the user commits the change. The parent owns the
   * downstream `changeMaster()` call and the modal it drives.
   */
  readonly onRotationStart: (currentPass: string, newPass: string) => void;
}

export function MasterPasswordTab({ onRotationStart }: MasterPasswordTabProps) {
  const [currentPass, setCurrentPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [showGenerator, setShowGenerator] = useState(false);

  // zxcvbn must be memoized to avoid re-running per keystroke when
  // unrelated state (e.g. `confirmPass`) changes. Keyed only on the
  // new password.
  const newScore = useMemo(
    () => (newPass.length > 0 ? zxcvbn(newPass).score : 0),
    [newPass],
  );
  const newStrong = newScore >= 3;

  const allFilled =
    currentPass.length > 0 && newPass.length > 0 && confirmPass.length > 0;
  const sameAsCurrent = newPass === currentPass;
  const matches = newPass === confirmPass;
  const canSubmit = allFilled && newStrong && matches && !sameAsCurrent;

  function onSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!canSubmit) return;
    onRotationStart(currentPass, newPass);
  }

  return (
    <div className="max-w-xl space-y-6">
      <h2 className="text-xl font-medium">Master password</h2>
      <p className="max-w-[60ch] text-xs text-text-soft">
        Change the master password that protects this device's vault list and
        all synced vaults. There is{" "}
        <strong className="text-danger">no recovery</strong> — losing this
        password makes every synced vault unrecoverable.
      </p>

      <form className="space-y-4" onSubmit={onSubmit}>
        <div>
          <label
            htmlFor="master-current"
            className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-text-dim"
          >
            Current master
          </label>
          <PassphraseInput
            id="master-current"
            autoCompleteMode="current-password"
            aria-label="Current master"
            value={currentPass}
            onChange={(e) => setCurrentPass(e.target.value)}
          />
        </div>

        <div>
          <label
            htmlFor="master-new"
            className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-text-dim"
          >
            New master
          </label>
          <PassphraseInput
            id="master-new"
            autoCompleteMode="new-password"
            aria-label="New master"
            value={newPass}
            onChange={(e) => setNewPass(e.target.value)}
          />
          <PassphraseStrength passphrase={newPass} />
          {!newStrong && newPass.length > 0 && (
            <div className="mt-1.5 text-[12px] text-danger">
              Too weak — use 5+ random words.
            </div>
          )}
          {sameAsCurrent && newPass.length > 0 && (
            <div className="mt-1.5 text-[12px] text-danger">
              New master must differ from current.
            </div>
          )}
          <div className="mt-2">
            {!showGenerator ? (
              <button
                type="button"
                onClick={() => setShowGenerator(true)}
                className="text-[12px] text-accent-soft underline hover:text-text"
              >
                Generate a passphrase
              </button>
            ) : (
              <PassphraseGenerator onChoose={setNewPass} />
            )}
          </div>
        </div>

        <div>
          <label
            htmlFor="master-confirm"
            className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-text-dim"
          >
            Confirm new master
          </label>
          <PassphraseInput
            id="master-confirm"
            autoCompleteMode="new-password"
            aria-label="Confirm new master"
            value={confirmPass}
            onChange={(e) => setConfirmPass(e.target.value)}
          />
          {!matches && confirmPass.length > 0 && (
            <div className="mt-1.5 text-[12px] text-danger">
              Confirmation does not match.
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
        >
          Change master
        </button>
      </form>
    </div>
  );
}
